/**
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/PromptsAPI',
	'../../../DynamicFileLoader',
	'../../../lib/@waca/core-client/js/core-client/nls/StringResources',
	'../../../widgets/livewidget/nls/StringResources',
	'underscore'
], (APIFactory, PromptsAPI, DynamicFileLoader, StringResources, LiveWidgetStringResources, _) => {

	class Prompts {

		SEARCH_AND_SELECT = 'searchAndSelect';
		INPUT_BOX = 'inputBox';
		CALENDAR = 'calendar';
		RANGE = 'range';
		CANCEL = 'cancelPromptDialog';
		UNSUPPORTED_PROMPT_TYPE = 'unSupportedPromptType';
		PROMPT_PROPERTIES = ['name', 'capabilities', 'dataType', 'modelFilterItem'];
		SAVED_PROMPT_PROPERTIES = ['name', 'capabilities', 'dataType', 'values', 'modelFilterItem'];

		promptModules = {
			searchAndSelect: 'dashboard-analytics/features/dashboard/prompts/controls/SearchAndSelectPromptView',
			inputBox: 'dashboard-analytics/features/dashboard/prompts/controls/InputPromptView',
			calendar: 'dashboard-analytics/features/dashboard/prompts/controls/DateTimePromptView',
			range: 'dashboard-analytics/features/dashboard/prompts/controls/RangePromptView'
		}

		/**
		 * @classdesc  Prompt manager class to handle prompt related gestures.
		 * @constructs
		 * @public
		 *
		 */
		constructor(options) {
			this.dashboard = options.dashboardApi;
			this.logger = this.dashboard.getGlassCoreSvc('.Logger');

			// In progress prompt view
			this._inProgressMap = {};

			// Pending prompt cache
			this._pendingPrompts = [];

			this._timeZone = this.dashboard.getGlassCoreSvc('.UserProfile').preferences.timeZoneID || 'America/New_York';
		}

		destroy() {
			if (this.promptDialog) {
				this.promptDialog.destroy();
				this.promptDialog = null;
			}
			this.dashboard = null;
			this._pendingPrompts = null;
			this._timeZone = null;
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [PromptsAPI]);
			}
			return this.api;
		}

		/**
		 * @public
		 * @return true if the response status is prompt fault.
		 */
		isPromptFault(errorResponse) {
			return errorResponse.status === 412 && errorResponse.responseJSON && errorResponse.responseJSON.errors && errorResponse.responseJSON.errors.length;
		}

		openPromptView(specList, visualization) {
			if (!specList || !specList.length) {
				this.logger.error('Invalid prompt spec');
				return Promise.reject();
			}

			const dataSource = this._getDataSource(specList, visualization);
			if (!dataSource) {
				// Couldn't find right data source. Throw unsupported error.
				this.logger.error('Cannon find related data source for selected prompts');
				return this._onUnSupportedPrompt();
			}

			const infoList = this._buildPromptValueList(specList, dataSource.getId(), visualization);
			// If the visualization is preview and there are still unresolved prompts, reject here, Explore will display static view for this preview

			if (infoList.length > 0 && visualization.isPreview()) {
				const hasUnresolvedPrompt = infoList.some(info => {
					return !info.values;
				});
				if (hasUnresolvedPrompt) {
					return Promise.reject(new Error('promptingIsDisabled'));
				}
			}

			// Array of prompt promises when drag and drop multiple prompt items at the same.
			const valuePromiseList = _.pluck(infoList, 'promptValuesPromise');
			const specNameList = _.pluck(infoList, 'name');

			return Promise.all(valuePromiseList)
				.then((resultList) => {
					const promptViewPromiseList = [];
					resultList.forEach((result, index) => {
						const dlgOptions = {};
						dlgOptions.info = infoList[index];
						dlgOptions.queryResult = result; // could be QueryResultAPI or undefined depends on what type of prompt is
						dlgOptions.dialogName = 'dashboard-analytics/lib/@waca/core-client/js/core-client/ui/dialogs/GenericViewDialog';
						if (infoList[index].showView) {
							promptViewPromiseList.push(this._showPromptView(dlgOptions, specNameList, dataSource.getId()));
						} else {
							promptViewPromiseList.push(Promise.resolve(infoList[index]));
						}

					});

					return Promise.all(promptViewPromiseList)
						.then(resolvedPrompts => {
							let parameterValues = [];
							_.each(resolvedPrompts, resolvedPrompt => {
								parameterValues.push(resolvedPrompt);
							});

							return parameterValues;
						})
						.catch(error => {
							if (error && error.name) {
								this._removeInProgressAndExecuteNext(error.name);
							}
							throw error; // re-throw
						});
				})
				.catch(jqXHR => {
				// Possible query Error. Need to clean local cache.
					const keys = _.keys(this._inProgressMap);
					if (keys && keys.length) {
					// There is only one entry in cache
						this._removeInProgressAndExecuteNext(keys[0]);
					}

					throw jqXHR;
				});
		}

		getPromptSpecList(errorResponse) {
			const error = errorResponse.responseJSON.errors[0];
			if (!error || !error.code) {
				return;
			}

			const params = error.parameters;
			if (!params || params.length === 0) {
				return;
			}

			const paramInfoList = _.map(params, param => {
				param.value.name = param.name;
				return param.value;
			});
			return paramInfoList;
		}

		_getDataSource(specList, visualization) {
			let dataSource = null;
			if (visualization) {
				dataSource = visualization.getDataSource();
			} else {
				const modelFilterItem = _.find(specList, spec => spec.modelFilterItem);
				if (modelFilterItem) {
					const dataSources = this.dashboard.getFeature('DataSources').getDataSourceList();
					dataSource = dataSources.forEach(dataSource => {
						return dataSource.getMetadataColumn(modelFilterItem);
					});
				}
			}

			return dataSource;
		}

		// TODO remove onCancelPromptDialog and onUnSupportedPrompt from DeprecatedWidgetBase once
		// new API is live
		_onCancelPromptDialog() {
			this.dashboard.undo();
		}

		_onUnSupportedPrompt() {
			DynamicFileLoader.load(['dashboard-analytics/lib/@waca/core-client/js/core-client/ui/dialogs/ConfirmationDialog'])
				.then(modules => {
					let dlg = new modules[0]('warning', '', LiveWidgetStringResources.get(this.UNSUPPORTED_PROMPT_TYPE));
					dlg.confirm(this._onCancelPromptDialog.bind(this), this._onCancelPromptDialog.bind(this));
				});
			return Promise.reject(new Error(this.UNSUPPORTED_PROMPT_TYPE));
		}

		_buildPromptValueList(specList, dataSourceId, visualization) {
			let infoList = [];
			const savedPrompts = visualization && visualization.getSavedPrompts();
			_.each(specList, spec => {
				const savedSpec = savedPrompts && savedPrompts.getPromptSpec(spec.name);
				let info;
				if (savedSpec) {
					info = _.pick(savedSpec, this.SAVED_PROMPT_PROPERTIES);
					if (spec.capabilities.optional && info.values[0] && info.values[0].mun) {
						info.values[0] = info.values[0].mun;
						info.showView = false;
					} else if (spec.rePrompt) {
						// Flag used to differentiate re-prompt or re-open saved dashboard.
						// Saved prompts should not be re-prompted unless click on edit prompt button.
						// showView is undefined by default.
						info.showView = true;
						info.rePrompt = true;
						this._initViewInfo(dataSourceId, info, spec);
					}

				} else {
					info = _.pick(spec, this.PROMPT_PROPERTIES);
					// Add optional prompt default value and save it
					if (spec.capabilities.optional) {
						info.values = ['nil'];
						info.showView = false;
					} else {
						info.showView = true;
						this._initViewInfo(dataSourceId, info, spec);
					}
				}
				if (!info.showView) {
					info.promptValuesPromise = Promise.resolve();
				}
				infoList.push(info);
			});

			return infoList;
		}

		_initViewInfo(dataSourceId, info, requestSpec) {
			info.promptModuleName = this._getPromptModuleName(requestSpec);
			info.columnId = info.modelFilterItem;
			if (!this.queryService) {
				this.queryService = this.dashboard.getFeature('QueryService');
			}

			switch (info.promptModuleName) {
				case this.SEARCH_AND_SELECT:
					info.promptValuesPromise = this.queryService.getColumnValues(dataSourceId, info.columnId);
					break;
				case this.RANGE:
					info.promptValuesPromise = this.queryService.getColumnMinMaxValues(dataSourceId, info.columnId);
					break;
				default:
					info.promptValuesPromise = Promise.resolve();
			}
		}

		/*
		 * Returns the prompt dialog type.
		 */
		_getPromptModuleName(spec) {
			let promptModuleName;
			switch (spec.dataType) {
				case 'xsdDate':
				case 'xsdDateTime':
				case 'xsdTime':
					if (spec.capabilities.multivalued && spec.capabilities.discreteValue && spec.modelFilterItem) {
						promptModuleName = this.SEARCH_AND_SELECT;
					} else {
						promptModuleName = this.CALENDAR;
					}
					break;

				case 'xsdInt':
				case 'xsdLong':
				case 'xsdShort':
				case 'xsdFloat':
				case 'xsdDecimal':
				case 'xsdDouble':
					if (spec.modelFilterItem) {
						if (spec.capabilities.multivalued) {
							if (spec.capabilities.discreteValue) {
								promptModuleName = this.SEARCH_AND_SELECT;
							} else {
								promptModuleName = this.RANGE;
							}
						} else {
							promptModuleName = this.INPUT_BOX;
						}
					} else {
						promptModuleName = this.INPUT_BOX;
					}
					break;

				case 'xsdString':
					if (spec.modelFilterItem) {
						promptModuleName = this.SEARCH_AND_SELECT;
					} else {
						promptModuleName = this.INPUT_BOX;
					}
					break;

				default:
					promptModuleName = this.INPUT_BOX;

			}

			return promptModuleName;
		}

		_showPromptView(dlgOptions, specNameList, dataSourceId, pendingViewPromise) {
			const id = dlgOptions.info.name;
			let promptViewPromise = this._inProgressMap[id];
			if (!promptViewPromise) {
				promptViewPromise = pendingViewPromise || Promise.defer();
				dlgOptions.promptViewPromise = promptViewPromise;
				if (this._isPrompting()) {
					// Pending prompt won't return a promise until its turn. Pending prompt view promise is
					// added to local cache in this._executeNextPrompt()
					let pendingPrompt = _.find(this._pendingPrompts, prompt => prompt.id === id);
					if (pendingPrompt) {
						promptViewPromise = pendingPrompt.pendingViewPromise;
					} else {
						pendingPrompt = {
							id: id,
							promptFunction: this._showPromptView.bind(this, dlgOptions, specNameList, dataSourceId, promptViewPromise),
							pendingViewPromise: promptViewPromise
						};
						this._pendingPrompts.push(pendingPrompt);

					}
				} else {
					this._inProgressMap[id] = promptViewPromise;
					this._prepareAndOpenDialog(dlgOptions, specNameList, dataSourceId);
				}
			}
			return promptViewPromise.promise;
		}

		_prepareAndOpenDialog(dlgOptions, specNameList, dataSourceId) {
			const label = dlgOptions.info.caption || dlgOptions.info.name;
			dlgOptions.viewTitle = StringResources.get('promptControlTitle', {
				paramLabel: label
			});
			dlgOptions.viewOptions = _.pick(dlgOptions.info, [
				'promptModuleName',
				'name',
				'dataType',
				'capabilities'
			]);
			dlgOptions.viewOptions.viewTitle = dlgOptions.viewTitle;

			switch (dlgOptions.info.promptModuleName) {
				case this.INPUT_BOX:
					dlgOptions.viewOptions.label = label;
					break;

				case this.CALENDAR:
					dlgOptions.viewOptions.columnId = dlgOptions.info.modelFilterItem;
					dlgOptions.viewOptions.timezone = this._timeZone;
					break;

				case this.SEARCH_AND_SELECT: {
					dlgOptions.viewOptions.columnId = dlgOptions.info.modelFilterItem;
					dlgOptions.viewOptions.singleSelect = !dlgOptions.info.capabilities.multivalued;
					dlgOptions.viewOptions.queryResult = dlgOptions.queryResult;
					dlgOptions.viewOptions.getColumnValues = this.queryService.getColumnValues.bind(this, dataSourceId);
					break;
				}
				case this.RANGE: {
					dlgOptions.viewOptions.columnId = dlgOptions.info.modelFilterItem;
					dlgOptions.viewOptions.queryResult = dlgOptions.queryResult;
					dlgOptions.viewOptions.getColumnMinMaxValues = this.queryService.getColumnMinMaxValues.bind(this, dataSourceId);
					break;
				}

				default:
			}

			return this._openPromptDialog(dlgOptions, specNameList);
		}

		_openPromptDialog(dlgOptions, specNameList) {
			return DynamicFileLoader.load([this.promptModules[dlgOptions.info.promptModuleName]])
				.then(viewModule => {
					const dlgClassOptions = {
						showCloseX: false,
						buttons: [{
							text: StringResources.get('ok'),
							handler: this.onOk.bind(this, dlgOptions),
							type: 'primary',
							defaultId: 'ok_button'
						}, {
							text: StringResources.get('cancel'),
							handler: this.onCancel.bind(this, dlgOptions, specNameList),
							type: 'secondary',
							defaultId: 'cancel_button'
						}],
						title: dlgOptions.viewOptions.viewTitle,
						titleAriaLabel: StringResources.get('promptDialogTitle', {
							paramLabel: dlgOptions.label
						}),
						viewClass: viewModule[0],
						viewOptions: dlgOptions.viewOptions
					};

					if (dlgOptions.info.values) {
						dlgOptions.viewOptions.defaultValues = dlgOptions.info.values;
					}
					dlgOptions.viewOptions.enableOk = this.enableOk.bind(this);
					return DynamicFileLoader.load([dlgOptions.dialogName]).then(modules => {
						const PromptDialog = modules[0];
						this.promptDialog = new PromptDialog(dlgClassOptions);
						this.promptDialog.open();
					});
				})
				.then(() => {
					if (this.promptDialog.viewOptions.promptModuleName !== this.RANGE &&
					this.promptDialog.viewOptions.promptModuleName !== this.CALENDAR) {
						this.promptDialog.disableOk();
					}
				})
				.catch((error) => {
					this.logger.error('Could not open prompt dialog', error);
					dlgOptions.promptViewPromise.reject(_.extend(error, {
						name: dlgOptions.info.name
					}));
				});
		}

		onOk(options) {
			this._removeInProgressAndExecuteNext(options.info.name);
			let promptValueList;
			if (this.promptDialog.view.getPromptValues) {
				promptValueList = this.promptDialog.view.getPromptValues();
			} else {
				promptValueList = [];
			}

			let resolvedSpec = options.info;
			resolvedSpec.values = _.map(promptValueList, value => {
				if (typeof value === 'object') {
					return _.pick(value, ['value', 'label']);
				} else {
					return {
						'value': value,
						'label': value
					};
				}

			});

			this.promptDialog.hide();
			return options.promptViewPromise.resolve(resolvedSpec);
		}

		onCancel(options, specNameList) {
			// Cancel one prompt dialog should trigger cancel of all requried prompt actions since
			// all of the non-optional prompts are required for the query to run.
			this._cleanPromptCache(options.info.name, specNameList);

			this.promptDialog.hide();
			if (!options.info.rePrompt) {
				// drag and drop prompts are combined actions need to undo this action.
				// E.g. Dnd prompts to local filter, slots, canvas, or tab filter zone triggers multiple
				// events. If cancel happened in on Dnd prompts, need to undo the actions.
				// But in case of re-prompts, only need to close the prompts dialog.
				this._onCancelPromptDialog();
			}

			const error = new Error(this.CANCEL);
			error.name = options.info.name;
			return options.promptViewPromise.reject(error);
		}

		enableOk(bEnabled) {
			this.promptDialog.enableOk(bEnabled);
		}

		_isPrompting() {
			return _.size(this._inProgressMap) > 0;
		}

		_executeNextPrompt() {
			if (this._pendingPrompts.length > 0) {
				const nextPrompt = this._pendingPrompts.shift();
				return nextPrompt.promptFunction();
			}
		}

		_removeInProgressAndExecuteNext(id) {
			delete this._inProgressMap[id];
			this._executeNextPrompt();
		}

		_cleanPromptCache(id, specNameList) {
			// Cancel all current requests
			this._pendingPrompts = _.filter(this._pendingPrompts, pendingPrompt =>
				specNameList.indexOf(pendingPrompt.id) === -1
			);

			delete this._inProgressMap[id];
		}
	}

	return Prompts;

});
